// Data source for the game-wins / top-streaks leaderboard and the engagement
// (streak & rewards) summary.
// Live paths:
//  - POST /dashboard/v1/get_leaderboard, ranking_criteria='game_wins'|'streak_count'.
//  - POST /dashboard/v1/engagement-summary/.
// Set Environment.LEADERBOARD_USE_MOCK / ENGAGEMENT_USE_MOCK = true to fall
// back to the local simulated engine for offline UI work (no backend
// required) — the engagement-summary endpoint isn't deployed yet, so
// ENGAGEMENT_USE_MOCK defaults to true in env.local.js.

import ChessRestClient from '@/Api/ChessRestClient';
import Environment from '../../../environment';
import {
  mockLeaderboardEngine,
  mockEngagementSummary,
  mockWinners,
} from './mockData';
import {
  EngagementSummary,
  EngagementWinners,
  LeaderboardPrizeVM,
  LeaderboardSnapshot,
  MeWinVM,
  MilestoneVM,
  Period,
  RankingCriteria,
  TrendDir,
  WinnerPeriodType,
  WinnerSource,
  formatWinnerPeriodLabel,
} from './types';

export const USE_MOCK: boolean =
  (Environment as {LEADERBOARD_USE_MOCK?: boolean})?.LEADERBOARD_USE_MOCK ===
  true;

export const ENGAGEMENT_USE_MOCK: boolean =
  (Environment as {ENGAGEMENT_USE_MOCK?: boolean})?.ENGAGEMENT_USE_MOCK ===
  true;

interface ApiEntry {
  rank: number;
  userid: number;
  name: string;
  rating: number;
  avatar_url?: string;
  // ranking_criteria='game_wins'
  wins?: number;
  // ranking_criteria='streak_count' — same envelope, different metric key.
  streak_count?: number;
  trend?: TrendDir;
  trend_delta?: number | null;
}

interface ApiLeaderboardData {
  // 'daily' | 'weekly' for game_wins; 'current' for streak_count — we key off
  // our own requested `period`/`criteria`, not this field.
  period: string;
  total_players: number;
  leader_wins?: number;
  leader_streak_count?: number;
  entries: ApiEntry[];
  me: {
    userid: number;
    rank: number | null;
    wins?: number;
    streak_count?: number;
    rating: number;
    name: string;
    avatar_url?: string;
    in_top: boolean;
    trend?: TrendDir;
    trend_delta?: number | null;
  };
}

// game_wins rows/leader use `wins`/`leader_wins`; streak_count rows/leader use
// `streak_count`/`leader_streak_count` — same envelope shape otherwise.
const metricOf = (
  criteria: RankingCriteria,
  e: {wins?: number; streak_count?: number},
): number => (criteria === 'streak_count' ? e.streak_count ?? 0 : e.wins ?? 0);

function mapEnvelope(
  period: Period,
  criteria: RankingCriteria,
  payload: {success: boolean; message?: string; data?: ApiLeaderboardData},
): LeaderboardSnapshot {
  if (!payload?.success || !payload?.data) {
    throw new Error(payload?.message || 'leaderboard fetch failed');
  }
  const d = payload.data;
  const leaderMetric =
    criteria === 'streak_count' ? d.leader_streak_count : d.leader_wins;
  return {
    period,
    entries: (d.entries || []).map(e => ({
      userid: e.userid,
      rank: e.rank,
      name: e.name,
      rating: e.rating,
      wins: metricOf(criteria, e),
      avatarUrl: e.avatar_url || undefined,
      you: e.userid === d.me?.userid,
      trend: e.trend,
      trendDelta: e.trend_delta,
    })),
    me: {
      rank: d.me?.rank ?? null,
      wins: metricOf(criteria, d.me || {}),
      rating: d.me?.rating ?? 0,
      name: d.me?.name ?? '',
      avatarUrl: d.me?.avatar_url || undefined,
      inTop: !!d.me?.in_top,
      trend: d.me?.trend,
      trendDelta: d.me?.trend_delta,
    },
    totalPlayers: d.total_players ?? 0,
    leaderWins: leaderMetric ?? 0,
  };
}

export async function fetchLeaderboard(
  period: Period,
  topN: number,
  criteria: RankingCriteria = 'game_wins',
): Promise<LeaderboardSnapshot> {
  if (USE_MOCK) {
    return mockLeaderboardEngine.snapshot(period, criteria);
  }
  // duration is ignored server-side for ranking_criteria='streak_count' — send
  // a harmless default rather than the internal 'streaks' tab key.
  const duration = period === 'streaks' ? 'daily' : period;
  const response = await ChessRestClient.post('/dashboard/v1/get_leaderboard', {
    ranking_criteria: criteria,
    duration,
    leaderboard_type: 'global',
    top_n: topN,
  });
  return mapEnvelope(period, criteria, response.data);
}

// ---------------------------------------------------------------------------
// Engagement summary
// ---------------------------------------------------------------------------

interface ApiMilestone {
  id: string;
  days: number;
  period: string;
  days_left: number;
  achieved_this_period: boolean;
  prize: {
    code?: string;
    title: string;
    short: string;
    image_url?: string;
    description?: string;
  };
  // 'week_checkpoint' for the day-14/day-21 markers (week_2/week_3) + their
  // display label ("Week 2"/"Week 3") — see MilestoneVM.kind/label.
  kind?: string;
  label?: string;
}

// Weekly Top-3 leaderboard prize (Prizes v2) — additive, optional field on the
// engagement-summary envelope; absent when no active leaderboard-rank prize
// is configured for this segment.
interface ApiLeaderboardPrize {
  code?: string;
  title: string;
  min_metric_value: number;
  rank_from: number;
  rank_to: number;
}

interface ApiEngagementSummary {
  segment: 'classroom' | 'casual';
  streak: {
    streak_count: number;
    last_streak_date: string | null;
    today_complete: boolean;
    criteria: {
      id: number;
      name: string;
      description: string;
      target_count: number;
      user_count: number;
    }[];
  };
  milestones: ApiMilestone[];
  next_milestone: {
    id: string;
    days_left: number;
    prize_short: string;
    label?: string;
  } | null;
  leaderboard_prize?: ApiLeaderboardPrize;
  // Prizes v2 — any prize has ever been awarded; gates the Winners section.
  winners_available: boolean;
  page_enabled: boolean;
  server_date: string;
}

function mapMilestone(m: ApiMilestone): MilestoneVM {
  return {
    id: m.id,
    days: m.days,
    period: m.period,
    daysLeft: m.days_left,
    achievedThisPeriod: m.achieved_this_period,
    prize: {
      code: m.prize?.code || undefined,
      title: m.prize?.title ?? '',
      short: m.prize?.short ?? '',
      imageUrl: m.prize?.image_url || undefined,
      description: m.prize?.description || undefined,
    },
    kind: m.kind || undefined,
    label: m.label || undefined,
  };
}

function mapLeaderboardPrize(
  p?: ApiLeaderboardPrize,
): LeaderboardPrizeVM | undefined {
  if (!p) {
    return undefined;
  }
  return {
    code: p.code || undefined,
    title: p.title,
    minMetricValue: p.min_metric_value,
    rankFrom: p.rank_from,
    rankTo: p.rank_to,
  };
}

function mapEngagementEnvelope(payload: {
  success: boolean;
  message?: string;
  data?: ApiEngagementSummary;
}): EngagementSummary {
  if (!payload?.success || !payload?.data) {
    throw new Error(payload?.message || 'engagement summary fetch failed');
  }
  const d = payload.data;
  return {
    segment: d.segment,
    streak: {
      streakCount: d.streak?.streak_count ?? 0,
      lastStreakDate: d.streak?.last_streak_date ?? null,
      todayComplete: !!d.streak?.today_complete,
      criteria: (d.streak?.criteria || []).map(c => ({
        id: c.id,
        name: c.name,
        description: c.description,
        targetCount: c.target_count,
        userCount: c.user_count,
      })),
    },
    milestones: (d.milestones || []).map(mapMilestone),
    nextMilestone: d.next_milestone
      ? {
          id: d.next_milestone.id,
          daysLeft: d.next_milestone.days_left,
          prizeShort: d.next_milestone.prize_short,
          label: d.next_milestone.label || undefined,
        }
      : null,
    leaderboardPrize: mapLeaderboardPrize(d.leaderboard_prize),
    winnersAvailable: !!d.winners_available,
    pageEnabled: !!d.page_enabled,
    serverDate: d.server_date,
  };
}

export async function fetchEngagementSummary(): Promise<EngagementSummary> {
  if (ENGAGEMENT_USE_MOCK) {
    return mockEngagementSummary();
  }
  const response = await ChessRestClient.post(
    '/dashboard/v1/engagement-summary/',
    {},
  );
  return mapEngagementEnvelope(response.data);
}

// ---------------------------------------------------------------------------
// Winners (Prizes v2 P3: POST /dashboard/v1/engagement-winners/)
// ---------------------------------------------------------------------------

interface ApiWinnerPrize {
  code?: string;
  title: string;
  short: string;
  image_url?: string;
  description?: string;
}

interface ApiWinner {
  userid: number;
  name: string;
  avatar_url?: string;
  source: WinnerSource;
  rank: number | null;
  // null for source='draw' — the backend never records a metric for draw
  // winners today (see WinnerVM.metricValue / WinnersSection's rendering).
  metric_value: number | null;
  prize: ApiWinnerPrize;
}

interface ApiWinnerPeriod {
  period_type: WinnerPeriodType;
  // NOT period_key — get_engagement_winners_periods groups by (period_type,
  // period_start_date) and never emits a period_key on this payload.
  period_start_date: string;
  period_end_date: string;
  winners: ApiWinner[];
}

interface ApiMeWin {
  period_type: WinnerPeriodType;
  period_start_date: string;
  period_end_date: string;
  prize: ApiWinnerPrize;
  rank: number | null;
}

interface ApiEngagementWinners {
  periods: ApiWinnerPeriod[];
  // `me.userid` mirrors get_leaderboard's envelope (see ApiLeaderboardData.me
  // above) — same "compare each row's userid against me.userid" convention,
  // used here to compute each winner's `you` flag. Flag to backend if the
  // deployed endpoint omits it: the YOU tag/highlight silently degrades off.
  me: {has_won: boolean; wins: ApiMeWin[]; userid?: number};
  server_date: string;
}

function mapWinnerPrize(p: ApiWinnerPrize) {
  return {
    code: p?.code || undefined,
    title: p?.title ?? '',
    short: p?.short ?? '',
    imageUrl: p?.image_url || undefined,
    description: p?.description || undefined,
  };
}

function mapWinnerPeriod(p: ApiWinnerPeriod, meUserid?: number) {
  return {
    periodType: p.period_type,
    periodStart: p.period_start_date,
    periodEnd: p.period_end_date,
    label: formatWinnerPeriodLabel(
      p.period_type,
      p.period_start_date,
      p.period_end_date,
    ),
    winners: (p.winners || []).map(w => ({
      userid: w.userid,
      name: w.name,
      avatarUrl: w.avatar_url || undefined,
      source: w.source,
      rank: w.rank,
      metricValue: w.metric_value ?? null,
      prize: mapWinnerPrize(w.prize),
      you: meUserid != null && w.userid === meUserid,
    })),
  };
}

function mapMeWin(w: ApiMeWin): MeWinVM {
  return {
    periodType: w.period_type,
    periodStartDate: w.period_start_date,
    periodEndDate: w.period_end_date,
    prize: mapWinnerPrize(w.prize),
    rank: w.rank ?? null,
  };
}

// Exported so mockData.ts's mock winners can be built as snake_case
// API-shaped payloads and pushed through this SAME mapper, instead of
// hand-assembling already-camelCase VMs — a renamed/removed contract field
// then trips in mock mode too, not just against the real API.
export function mapEngagementWinnersEnvelope(payload: {
  success: boolean;
  message?: string;
  data?: ApiEngagementWinners;
}): EngagementWinners {
  if (!payload?.success || !payload?.data) {
    throw new Error(payload?.message || 'engagement winners fetch failed');
  }
  const d = payload.data;
  return {
    periods: (d.periods || []).map(p => mapWinnerPeriod(p, d.me?.userid)),
    me: {
      hasWon: !!d.me?.has_won,
      wins: (d.me?.wins || []).map(mapMeWin),
    },
    serverDate: d.server_date,
  };
}

export async function fetchEngagementWinners(): Promise<EngagementWinners> {
  if (ENGAGEMENT_USE_MOCK) {
    return mockWinners();
  }
  const response = await ChessRestClient.post(
    '/dashboard/v1/engagement-winners/',
    {},
  );
  return mapEngagementWinnersEnvelope(response.data);
}
